import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper, flexRender, getCoreRowModel, PaginationState, useReactTable
} from '@tanstack/react-table';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { requireAuth } from '@/app/lib/auth-guard';
import { ApplicationsListItem } from '@/app/network/models/v3/applications';
import { formatApplicationStatus } from '@/applications/applications';
import { useApplicationsList, useCancelApplication } from '@/applications/queries';
import { Button } from '@/common/components/button';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Heading } from '@/common/components/heading';
import { Nullish } from '@/common/components/nullish';
import { Spinner } from '@/common/components/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/common/components/table';
import { TablePagination } from '@/common/components/table-pagination';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/common/components/tooltip';
import { FileLinesIcon } from '@/icons/fa/duotone-solid';
import { TrashIcon } from '@/icons/fa/regular';
import { ContentSection } from '@/shared/components/content-section';
import { EmptyState, EmptyStateMessage } from '@/shared/components/empty-states';
import { LiveData } from '@/shared/components/live-data';
import { SectionHeader } from '@/shared/components/sections';
import { Toast } from '@/shared/components/toast';
import { seo } from '@/shared/seo';

const applicationsSearchSchema = z.object({
  page: z.number().int().positive().nullish(),
  rpp: z.number().int().positive().nullish()
});

export const Route = createFileRoute('/applications/')({
  beforeLoad: async (ctx) => {
    await requireAuth(ctx);
  },
  validateSearch: applicationsSearchSchema,
  head: () => ({
    meta: seo({ title: 'My Applications' })
  }),
  component: RouteComponent,
  staticData: {
    headerSection: null
  }
});

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_ROWS_PER_PAGE = 10;

const columnHelper = createColumnHelper<ApplicationsListItem>();

function RouteComponent() {
  const searchParams = Route.useSearch();
  const page = searchParams.page ?? DEFAULT_PAGE_NUMBER;
  const { data: applicationsData, isPending, error } = useApplicationsList({ page, rpp: DEFAULT_ROWS_PER_PAGE });

  React.useEffect(() => {
    if (error) {
      toast.error(Toast, {
        data: {
          title: 'Unable to Load Applications',
          content: error?.message ?? 'Please try again later.'
        }
      });
    }
  }, [error]);

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <ContentSection bg="white" fullWidth>
        <SectionHeader>
          <Heading>My Applications</Heading>
          <Button to="/apply">New Application</Button>
        </SectionHeader>

        <LiveData
          data={applicationsData?.items}
          isLoading={isPending}
          loadingState={<Spinner />}
          fallback={<ApplicationsEmptyState />}
        >
          {data => (
            <ApplicationsTable
              data={data}
              totalResults={applicationsData?.totalResults ?? 0}
              pagination={{ pageIndex: page, pageSize: DEFAULT_ROWS_PER_PAGE }}
            />
          )}
        </LiveData>
      </ContentSection>
    </main>
  );
}

const columns = [
  columnHelper.accessor('serviceLocation', {
    cell: info => <Nullish value={info.getValue()} />,
    header: () => 'Service Location'
  }),
  columnHelper.accessor('status', {
    cell: info => formatApplicationStatus(info.getValue()),
    header: () => 'Status'
  }),
  columnHelper.accessor('createdAt', {
    cell: info => DateTime.fromISO(info.getValue()).toLocaleString(),
    header: () => 'Date Submitted'
  }),
  columnHelper.display({
    id: 'next-steps',
    cell: ({ row }) => (
      <Button plain>
        {/* TODO add actual continue next steps functionality */}
        {row.getValue('status') === 'worked_by_applicant' ? 'Continue' : 'Next Steps'}
      </Button>
    )
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      {
        // TODO: sb: meta is not the right approach here long-term. meta is
        //  better for adding data to many columns across many tables.
        const handleCancelApplicationClick = (table.options.meta as any)?.handleCancelApplicationClick;

        const id = row.original.id;
        const status = row.getValue('status');

        if (status === 'cancelled' || status === 'closed') {
          return null;
        }

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button plain onClick={() => handleCancelApplicationClick(id)}>
                <TrashIcon/>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Cancel Application
            </TooltipContent>
          </Tooltip>
        );
      }
    }
  })
];

type ApplicationsTableProps = {
  data: ApplicationsListItem[];
  totalResults: number;
  pagination: PaginationState;
};

function ApplicationsTable({ data, totalResults, pagination }: ApplicationsTableProps) {
  const { mutateAsync: cancelApplication } = useCancelApplication();

  const [currentApplicationId, setCurrentApplicationId] = useState<number | null>(null);
  const [cancelApplicationDialogIsOpen, setCancelApplicationDialogIsOpen] = useState(false);

  /**
   * Invoked when a user clicks the Cancel Application icon button.
   *
   * @param id The id of the application to cancel.
   */
  const handleCancelApplicationClick = (id: number) => {
    setCurrentApplicationId(id);
    setCancelApplicationDialogIsOpen(true);
  };

  /**
   * Invoked when a user dismisses the Cancel Application dialog without taking
   * action.
   */
  const handleCloseCancelApplicationDialog = () => {
    setCurrentApplicationId(null);
    setCancelApplicationDialogIsOpen(false);
  };

  /**
   * Invoked when a user confirms the cancellation of their application via the
   * dialog box.
   */
  const handleCancelApplication = async () => {
    console.log('currentApplicationId', currentApplicationId);
    if (!currentApplicationId) return;

    await cancelApplication(currentApplicationId!, {
      onSuccess: () => {
        setCancelApplicationDialogIsOpen(false);
        setCurrentApplicationId(null);
      }
    });
  };

  const table = useReactTable({
    data,
    rowCount: totalResults,
    columns,
    state: { pagination },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      handleCancelApplicationClick: handleCancelApplicationClick
    }
  });

  return (
    <div className="-mx-4 mt-8 sm:-mx-0">
      <Table>
        <TableHead>
          {
            table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHeader key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHeader>
                ))}
              </TableRow>
            ))
          }
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        className="mt-4"
        page={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        totalResults={totalResults}
        searchParams={new URLSearchParams()}
        totalPages={table.getPageCount()}
      />
      <Dialog
        open={cancelApplicationDialogIsOpen}
        onClose={handleCloseCancelApplicationDialog}
      >
        <DialogTitle>Cancel Application</DialogTitle>
        <DialogDescription className="text-base font-normal">
          Are you sure you want to cancel the application?
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={handleCloseCancelApplicationDialog}>Cancel</Button>
          <Button onClick={handleCancelApplication}>
            Cancel Application
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function ApplicationsEmptyState() {
  return (
    <EmptyState>
      <FileLinesIcon className="size-20 text-gray-300"/>
      <Heading>No Applications</Heading>
      <EmptyStateMessage>
        You have no applications yet. Create one to get started.
      </EmptyStateMessage>
    </EmptyState>
  );
}
